import React, { Component, PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'
import { Box, BoxProps, Breakpoint, Grow, styled, SxProps, Theme, Typography } from '@mui/material'
import EventEmitter from 'events'
import { UIBtnBack, UIBtnClose } from './ui.units'
import ModalRouteStack from './stack'
import { modalRouteClasses, modalRouteSize } from './helpers'

type TOriginalEvent = React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent

export class EventClose extends CustomEvent<{ originalEvent: TOriginalEvent }> {}

interface EventMap {
  close: [EventClose]
}

interface IOptions<IQueryParam> {
  keyUseds?: (keyof IQueryParam)[]
}

function CreateModalRouteContainer<IQueryParam = Object>(options?: IOptions<IQueryParam>) {
  interface IProps {
    title?: React.ReactNode
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    fullHeight?: boolean
    disabledBackdrop?: boolean
    disabledBtnClose?: boolean
    disabledBtnBack?: boolean
    disabledTopBarLeft?: boolean
    disabledTopBarRight?: boolean
    onClose?: (e: EventClose) => void
    slotProps?: {
      wrap?: {
        sx?: BoxProps['sx']
      }
      content?: {
        sx?: BoxProps['sx']
      }
    }
    keyUseds?: (keyof IQueryParam)[]
    topbar?: {
      left?: React.ReactNode
      right?: React.ReactNode
    }
  }

  class ModalRouteContainer extends Component<PropsWithChildren<IProps>> {
    refLink: HTMLAnchorElement | undefined | null
    events: EventEmitter<EventMap>
    constructor(props: IProps) {
      super(props)
      this.events = new EventEmitter<EventMap>({})
      this.props.onClose && this.events.addListener('close', this.props.onClose)
      this.events.addListener('close', this.handleBgClickDefault)
    }

    shouldComponentUpdate(nextProps: Readonly<React.PropsWithChildren<IProps>>, nextState: Readonly<{}>, nextContext: any): boolean {
      if (this.props.onClose && nextProps.onClose !== this.props.onClose) {
        this.events.removeListener('close', this.props.onClose)
      }
      return true
    }

    componentDidMount() {
      document.addEventListener('keydown', this.handleKeyDown)
    }

    componentWillUnmount() {
      document.removeEventListener('keydown', this.handleKeyDown)
      this.events.removeAllListeners()
    }

    render() {
      const backLink = this.getBackLink() ?? ''
      return (
        <>
          <Link ref={(ref) => (this.refLink = ref)} hidden to={backLink} />
          <Wrap className={this.getClasses()} sx={this.props.slotProps?.wrap?.sx}>
            <BgBehaind onMouseDown={this.onBgrClick} />
            <Grow in mountOnEnter unmountOnExit>
              <WrapContent maxwithsize={this.props.size}>
                <WrapTopbar className={modalRouteClasses.topbar}>
                  {this.props.disabledTopBarLeft !== true && (
                    <div className={modalRouteClasses.topbarLeft}>
                      {this.props.disabledBtnBack !== true && <UIBtnBack onClick={this.handleBack} />}
                      {this.props.topbar?.left}
                    </div>
                  )}
                  {this.renderHeader()}
                  {this.props.disabledTopBarRight !== true && (
                    <div className={modalRouteClasses.topbarRight}>
                      {this.props.topbar?.right}
                      {this.props.disabledBtnClose !== true && <UIBtnClose onClick={this.handleBack} />}
                    </div>
                  )}
                </WrapTopbar>
                <Box className={modalRouteClasses.innerContent} sx={this.props.slotProps?.content?.sx}>
                  {this.props.children}
                </Box>
              </WrapContent>
            </Grow>
          </Wrap>
        </>
      )
    }

    renderHeader = () => {
      const { title } = this.props
      if (title && typeof title === 'string') {
        return (
          <Typography variant='subtitle1' sx={{ fontWeight: 600, textAlign: 'center', flex: 1 }}>
            {title}
          </Typography>
        )
      }
      return title ?? <></>
    }

    getObjectEmpty = () => {
      const list = this.props.keyUseds ?? options?.keyUseds ?? []
      return list.reduce<{ [key in keyof IQueryParam]?: any }>((a, b) => {
        a[b] = ''
        return a
      }, {})
    }

    private onBack = () => {
      ModalRouteStack.PopStack({ query: this.getObjectEmpty() })
    }

    handleBgClickDefault = (ecl: EventClose) => {
      if (ecl.defaultPrevented) return
      const e = ecl.detail.originalEvent
      if (this.props.disabledBackdrop === true) return
      if (e.target === e.currentTarget) {
        const backLink = this.getBackLink()
        if (backLink !== undefined) {
          e.preventDefault()
          e.stopPropagation()
          const action = ModalRouteStack[backLink]
          action ? action() : this.refLink?.click()
        } else {
          this.onBack()
        }
      }
    }

    handleBack = () => {
      const backLink = this.getBackLink()
      if (backLink !== undefined) {
        const action = ModalRouteStack[backLink]
        action ? action() : this.refLink?.click()
      } else {
        this.onBack()
      }
    }

    onBgrClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
      const closeEvent = new EventClose('close', { detail: { originalEvent: e }, cancelable: true })
      this.events.emit('close', closeEvent)
    }

    handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') this.handleBack()
    }

    getBackLink = (): string | undefined => {
      // const data = this.props.back
      // if (!data) return undefined
      // if (IsEMdStack(data)) return data.toString()
      // return CreateRoutePath(data)
      return undefined
    }

    getClasses = () => {
      const classes = [modalRouteClasses.content]
      if (this.props.fullHeight) classes.push(modalRouteClasses.fullHeight)
      return classes.join(' ')
    }
  }

  return ModalRouteContainer
}

export default CreateModalRouteContainer

const Wrap = styled(Box)({
  flex: 1,
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  boxSizing: 'border-box',
  alignItems: 'center',
  padding: '10px 0',
  [`&.${modalRouteClasses.fullHeight}`]: {
    alignItems: 'stretch'
  }
})

const WrapTopbar = styled(Box)({
  height: modalRouteSize.heightHeaderPx,
  display: 'flex',
  alignItems: 'center',
  boxShadow: 'rgba(0, 0, 0, 0.03) 0px 3px 5px',
  position: 'relative',
  zIndex: 2,
  flex: '0 0 auto',
  [`& .${modalRouteClasses.topbarLeft}, & .${modalRouteClasses.topbarRight}`]: {
    position: 'absolute',
    top: 0,
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  [`& .${modalRouteClasses.topbarLeft}`]: {
    left: 0,
    paddingLeft: '12px'
  },
  [`& .${modalRouteClasses.topbarRight}`]: {
    justifyContent: 'flex-end',
    right: 0,
    paddingRight: '12px'
  }
})

const WrapContent = styled(Box)<{ maxwithsize?: keyof Breakpoint | string }>(({ theme, maxwithsize }) => {
  const { breakpoints } = theme
  let maxWidth: string | number = maxwithsize?.toString() ?? ''
  if (maxwithsize && typeof maxwithsize === 'string' && maxwithsize in breakpoints.values) {
    maxWidth = breakpoints.values[maxwithsize as keyof typeof breakpoints.values] ?? breakpoints.values.sm
  }
  return {
    position: 'relative',
    zIndex: 1,
    background: '#fff',
    borderRadius: '6px',
    boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: '100%',
    [`& .${modalRouteClasses.innerContent}`]: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto',
      overflowX: 'hidden',
      width: 'calc(100vw - 24px)',
      maxWidth,
      maxHeight: `calc(100vh - ${modalRouteSize.heightHeaderPx})`,
      minHeight: '240px'
    }
  }
})

const BgBehaind = styled(Box)({
  backgroundColor: '#00000095',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 0
})
